import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { applicantInitialState, addressInitialState } from './initialState';
import { type RootState } from './store';
import type { ApplicantsState, AddressState } from './types';

const initialState: ApplicantsState = {
    currentApplicantIndex: 0,
    applicants: [applicantInitialState],
};

/* Reducers */
export const applicantsSlice = createSlice({
  name: 'applicants',
  initialState,
  reducers: {
    initApplicant: (state, action: PayloadAction<number>) => {
      state.applicants[action.payload] = applicantInitialState;
    },

    addApplicant: (state) => {
      state.applicants.push(applicantInitialState);
    },

    setHomeLoanDetails: (
      state,
      action: PayloadAction<{
        applicationType: string;
        IsApplyingOnline: boolean;
        IsOwnerOccupiedLE: boolean;
        isExistingCustomer: boolean;
      }>
    ) => {
      state.applicants[0].homeLoanEnquiryDetails.ApplicationType =
        action.payload.applicationType;
      state.applicants[0].homeLoanEnquiryDetails.IsApplyingOnline =
        action.payload.IsApplyingOnline;
      state.applicants[0].homeLoanEnquiryDetails.IsOwnerOccupied =
        action.payload.IsOwnerOccupiedLE;
      state.applicants[0].homeLoanEnquiryDetails.IsExistingMember =
        action.payload.isExistingCustomer;
    },

    setHomeLoanApplyOnlineDetails: (
      state,
      action: PayloadAction<{
        applicationType: string;
      }>
    ) => {
      state.applicants[0].homeLoanEnquiryDetails.ApplicationType =
        action.payload.applicationType;
    },

    ////Home Loan Enquiry setters
    setHomeLoanApplicationTypeApplyOnline: (
      state,
      action: PayloadAction<{ applicationType: string }>
    ) => {
      state.applicants[0].homeLoanEnquiryDetails.ApplicationType =
        action.payload.applicationType;
    },

    setIsLenderEnquiry: (state, action: PayloadAction<string | null>) => {
      state.applicants[0].homeLoanEnquiryDetails.IsLenderEnquiry =
        action.payload;
    },

    setPropertyLoanType: (state, action: PayloadAction<string>) => {
      state.applicants[0].homeLoanEnquiryDetails.PropertyLoanType =
        action.payload;
    },

    setEnquiryType: (state, action: PayloadAction<string>) => {
      state.applicants[0].homeLoanEnquiryDetails.EnquiryType = action.payload;
    },
    setApplicationType: (state, action: PayloadAction<string>) => {
      state.applicants[0].homeLoanEnquiryDetails.ApplicationType =
        action.payload;
    },
    setIsOwnerOccupied: (state, action: PayloadAction<boolean>) => {
      state.applicants[0].homeLoanEnquiryDetails.IsOwnerOccupied =
        action.payload;
    },

    setApplicantIsExistingMember: (
      state,
      action: PayloadAction<boolean | null>
    ) => {
      state.applicants[0].homeLoanEnquiryDetails.IsExistingMember =
        action.payload;
    },

    setOnlineEligibility: (state, action: PayloadAction<boolean>) => {
      state.applicants[0].homeLoanEnquiryDetails.OnlineEligibility =
        action.payload;
    },

    setCampaignCode: (state, action: PayloadAction<string>) => {
      state.applicants[0].homeLoanEnquiryDetails.CampaignCode = action.payload;
    },

    ////Personal Detail setters
    setFirstName: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.firstName = action.payload;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.lastName = action.payload;
    },
    setMobileNumber: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.mobileNumber = action.payload;
    },

    setEmailAddress: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.emailAddress = action.payload;
    },

    setApplicantContactMethod: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.contactMethod = action.payload;
    },

    setResidentialAddress: (state, action: PayloadAction<AddressState>) => {
      state.applicants[0].aboutYou.residentialAddress = {
        ...addressInitialState,
        ...action.payload,
      };
    },

    setAgeRange: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.ageRange = action.payload;
    },

    //Employment and loans
    setEmploymentStatus: (
      state,
      action: PayloadAction<{
        employmentStatus: string;
      }>
    ) => {
      state.applicants[state.currentApplicantIndex].aboutYou.employmentStatus =
        action.payload.employmentStatus;
    },

    setBorrowingAmount: (state, action: PayloadAction<number>) => {
      state.applicants[0].financialDetails.homeLoanBorrowingAmount =
        action.payload;
    },

    setAdditionalFunds: (state, action: PayloadAction<number>) => {
      state.applicants[0].financialDetails.homeLoanAdditionalFunds =
        action.payload;
    },

    setGeneralMessage: (state, action: PayloadAction<string>) => {
      state.applicants[0].generalEnquiryDetails.generalMessage = action.payload;
    },
    setApplicantContactMethodGeneral: (
      state,
      action: PayloadAction<string>
    ) => {
      state.applicants[0].aboutYou.contactMethodGeneral = action.payload;
    },

    setIsExistingCustomer: (state, action: PayloadAction<boolean>) => {
      state.applicants[0].aboutYou.isExistingCustomer = action.payload;
    },

    setCustomerId: (state, action: PayloadAction<string>) => {
      state.applicants[0].aboutYou.customerId = action.payload;
    },

    setGrossSalary: (state, action: PayloadAction<number>) => {
      state.applicants[0].aboutYou.grossSalary = action.payload;
    },

    // Personal Loan setters
    setPersonalLoanBorrowingAmount: (state, action: PayloadAction<number>) => {
      state.applicants[0].financialDetails.personalLoanBorrowingAmount =
        action.payload;
    },

    setPersonalLoanBorrowingPurpose: (state, action: PayloadAction<string>) => {
      state.applicants[0].financialDetails.personalLoanBorrowingPurpose =
        action.payload;
    },

    setPersonalLoanHousingSituation: (state, action: PayloadAction<string>) => {
      state.applicants[0].financialDetails.personalLoanHousingSituation =
        action.payload;
    },

    setPersonalLoanHasExistingHomeLoan: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.applicants[0].financialDetails.personalLoanHasExistingHomeLoan =
        action.payload;
    },
  },
});

export const {
    initApplicant,
    addApplicant,

    setHomeLoanDetails,
    setHomeLoanApplyOnlineDetails,

    setHomeLoanApplicationTypeApplyOnline,
    setIsLenderEnquiry,
    setApplicantIsExistingMember,
    setEnquiryType,

  setPropertyLoanType,
  setApplicationType,
  setIsOwnerOccupied,
  setOnlineEligibility,
  setCampaignCode,
  setFirstName,
  setLastName,
  setMobileNumber,
  setEmailAddress,
  setApplicantContactMethod,
  setResidentialAddress,
  setAgeRange,
  setGeneralMessage,
  setApplicantContactMethodGeneral,
  setIsExistingCustomer,
  setCustomerId,
  setGrossSalary,

    setEmploymentStatus,
    setBorrowingAmount,
    setAdditionalFunds,

    setPersonalLoanBorrowingAmount,
    setPersonalLoanBorrowingPurpose,
    setPersonalLoanHousingSituation,
    setPersonalLoanHasExistingHomeLoan,
} = applicantsSlice.actions;

export const selectApplicants = (state: RootState): ApplicantsState =>
    state.applicants;

export default applicantsSlice.reducer;
